/**
 * Sticky bottom nav: progress bar (15 segments), "Complete!" badge, "View Results" button.
 * When all items are rated, onViewResults (if provided) runs instead of scrolling to #results.
 */
import React, { useEffect, useState } from 'react';

import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { SegmentTooltip } from './SegmentTooltip';
import type { ChecklistSection, ChecklistState } from '../types';
import { cn } from '@/lib/utils';

interface SectionNavProps {
  sections: ChecklistSection[];
  state: ChecklistState;
  /** When all items are complete, clicking "View results" calls this instead of scrolling. */
  onViewResults?: () => void;
}

export const SectionNav: React.FC<SectionNavProps> = ({ sections, state, onViewResults }) => {
  const allItems = sections.flatMap((s) => s.items);
  const totalItems = allItems.length;
  const answeredItems = allItems.filter((item) => {
    const rating = state[item.id]?.rating;
    return rating !== null && rating !== undefined && typeof rating === 'number';
  }).length;
  const isComplete = answeredItems === totalItems;
  /** Delayed to allow initial paint with opacity-0 so transition runs. */
  const [showCompleteUI, setShowCompleteUI] = useState(false);

  /** After all items complete, reveal badge and "View Results" with a short fade/slide. */
  useEffect(() => {
    if (isComplete) {
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setShowCompleteUI(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setShowCompleteUI(false);
  }, [isComplete]);

  const scrollToItem = (itemId: string) => {
    const el = document.getElementById(itemId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  /** Either call onViewResults (go to results page) or scroll to #results. */
  const handleViewResultsClick = () => {
    if (onViewResults) {
      onViewResults();
    } else {
      const resultsSection = document.getElementById('results');
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] z-40"
      aria-label="Assessment progress"
      role="navigation"
    >
      <div className="max-w-container mx-auto px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 min-w-0 overflow-visible">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-text-primary" id="progress-label">
                Assessment Progress
              </span>
              <div className="flex items-center gap-2">
                {isComplete && (
                  <span
                    className={cn(
                      'inline-block transition-all duration-300 ease-out',
                      showCompleteUI ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-0.5'
                    )}
                  >
                    <Badge variant="success" className="text-sm font-medium" role="status">
                      Complete!
                    </Badge>
                  </span>
                )}
                <span
                  className="text-sm font-semibold text-primary tabular-nums"
                  aria-label={`${answeredItems} of ${totalItems} items completed`}
                >
                  {answeredItems}/{totalItems}
                </span>
              </div>
            </div>
            <div
              className="flex gap-0.5 h-[12px] overflow-visible bg-gray-100 border-0 p-0"
              role="progressbar"
              aria-valuenow={answeredItems}
              aria-valuemin={0}
              aria-valuemax={totalItems}
              aria-labelledby="progress-label"
            >
              {allItems.map((item, index) => {
                const answered =
                  state[item.id]?.rating !== null &&
                  state[item.id]?.rating !== undefined &&
                  typeof state[item.id]?.rating === 'number';
                const isFirst = index === 0;
                const isLast = index === allItems.length - 1;
                return (
                  <SegmentTooltip key={item.id} label={`${item.number}. ${item.title}`}>
                    <button
                      type="button"
                      onClick={() => scrollToItem(item.id)}
                      title={`${item.number}. ${item.title}`}
                      aria-label={`Question ${item.number}${answered ? ', answered' : ', not answered'}. Jump to question.`}
                      className={cn(
                        'progress-segment cursor-pointer w-full min-h-[12px] border-0 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
                        'appearance-none',
                        isFirst && 'rounded-l-md',
                        isLast && 'rounded-r-md',
                        !isFirst && !isLast && 'rounded-none',
                        answered
                          ? 'bg-[#2563eb] hover:bg-[#1d4ed8]'
                          : 'bg-[#e5e7eb] hover:bg-[#9ca3af]'
                      )}
                    />
                  </SegmentTooltip>
                );
              })}
            </div>
          </div>
          {isComplete && (
            <span
              className={cn(
                'inline-flex flex-shrink-0 transition-all duration-300 ease-out',
                showCompleteUI ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'
              )}
            >
              <Button
                onClick={handleViewResultsClick}
                size="sm"
                className="flex-shrink-0"
                aria-label={onViewResults ? 'View your results' : 'Scroll to assessment results'}
              >
                View Results
              </Button>
            </span>
          )}
        </div>
      </div>
    </nav>
  );
};
